import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase, db, isDemoMode } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user profile from database
  const fetchProfile = async (userId) => {
    if (isDemoMode) return null;
    try {
      const { data, error } = await db.getProfile(userId);
      if (error) throw error;
      setProfile(data);
      return data;
    } catch (err) {
      console.error('Error fetching profile:', err);
      return null;
    }
  };

  // Check auth state on mount and listen for changes
  useEffect(() => {
    // Mode démo - pas de Supabase
    if (isDemoMode) {
      setLoading(false);
      return;
    }

    // Get initial session
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          setUser(session.user);
          await fetchProfile(session.user.id);
        }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth event:', event);

        if (session?.user) {
          setUser(session.user);
          await fetchProfile(session.user.id);
        } else {
          setUser(null);
          setProfile(null);
        }
        setLoading(false);
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Register new user
  const register = async ({ email, password, firstName, lastName }) => {
    if (isDemoMode) {
      return { success: false, error: 'Mode démo: inscription désactivée. Configurez Supabase.' };
    }
    try {
      setError(null);
      const fullName = `${firstName} ${lastName}`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) throw error;

      // User created - profile will be created by trigger
      return { success: true, user: data.user };
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors de l\'inscription';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Login user
  const login = async ({ email, password }) => {
    if (isDemoMode) {
      return { success: false, error: 'Mode démo: connexion désactivée. Configurez Supabase.' };
    }
    try {
      setError(null);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      return { success: true, user: data.user };
    } catch (err) {
      let errorMessage = 'Erreur lors de la connexion';

      if (err.message?.includes('Invalid login credentials')) {
        errorMessage = 'Email ou mot de passe incorrect';
      } else if (err.message?.includes('Email not confirmed')) {
        errorMessage = 'Veuillez confirmer votre email avant de vous connecter';
      } else {
        errorMessage = err.message || errorMessage;
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Logout user
  const logout = async () => {
    if (isDemoMode) {
      setUser(null);
      setProfile(null);
      return;
    }
    try {
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Update user profile
  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await db.updateProfile(user.id, updates);
      if (error) throw error;

      setProfile(data);
      return { success: true, profile: data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    if (isDemoMode) return { success: false, error: 'Mode démo' };
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    if (isDemoMode) return { success: false, error: 'Mode démo' };
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Get user progress
  const getProgress = async () => {
    try {
      if (!user) return { data: [], error: null };
      const { data, error } = await db.getProgress(user.id);
      return { data: data || [], error };
    } catch (err) {
      return { data: [], error: err.message };
    }
  };

  // Update module progress
  const updateModuleProgress = async (moduleId, progress) => {
    try {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await db.updateProgress(user.id, moduleId, progress);
      return { success: !error, data, error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Complete module
  const completeModule = async (moduleId) => {
    return updateModuleProgress(moduleId, 100);
  };

  // Get certificate
  const getCertificate = async () => {
    try {
      if (!user) return { data: null, error: null };
      const { data, error } = await db.getCertificate(user.id);
      return { data, error };
    } catch (err) {
      return { data: null, error: err.message };
    }
  };

  // Generate certificate
  const generateCertificate = async () => {
    try {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await db.generateCertificate(user.id);
      return { success: !error, data, error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const value = {
    user,
    profile,
    loading,
    error,
    register,
    login,
    logout,
    updateProfile,
    resetPassword,
    updatePassword,
    getProgress,
    updateModuleProgress,
    completeModule,
    getCertificate,
    generateCertificate,
    isAuthenticated: !!user,
    isPremium: profile?.is_premium || profile?.has_paid || false,
    isAdmin: profile?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
